import React, { useMemo, useEffect } from 'react';
import { loadImportedTrades } from '../../utils/importedTradesStorage';
type DailyData = { pnl: number };

import type { TimeRange } from '../../types/dashboard';
import { filterTradesForDashboardRange } from '../../lib/trades/dashboardBucketAnalytics';
import {
  getTradePnl,
  getTradeTimestampMs,
  normalizeTradeStatus,
  type AnyTrade,
} from '../../lib/trades/authoritativeTradeData';

interface HoldTimeAnalysisWidgetProps {
  dailyData?: DailyData[];
  trades?: any[];
  timeRange?: TimeRange;
}

function getTradeDurationMins(trade: AnyTrade): number | null {
  if (normalizeTradeStatus(trade) === 'open') return null;

  const explicit = trade?.durationMinutes ?? trade?.durationMins ?? trade?.duration_min ?? trade?.minutesHeld ?? trade?.duration;
  if (explicit != null && explicit !== '') {
    const num = Number(explicit);
    if (Number.isFinite(num) && num >= 0) return num;
  }

  const openMs = getTradeTimestampMs(trade);
  const closeMs = trade?.closeTimestampMs ?? trade?.exitTimestampMs ?? trade?.exitTimestamp ?? trade?.exitTime ?? trade?.closeTime ?? trade?.closedAt;
  const closeTs = closeMs != null && closeMs !== '' ? Number(closeMs) : null;

  if (openMs != null && closeTs != null && Number.isFinite(closeTs) && closeTs >= openMs) {
    return Math.max(0, (closeTs - openMs) / 60000);
  }

  return null;
}

const normalizeZero = (val: number) => (val === 0 || Object.is(val, -0) ? 0 : val);

export const HoldTimeAnalysisWidget: React.FC<HoldTimeAnalysisWidgetProps> = ({ trades = [], timeRange = 'month' }) => {
  const activeTrades = useMemo(() => {
    if (Array.isArray(trades) && trades.length > 0) {
      return filterTradesForDashboardRange(trades as Record<string, any>[], timeRange as any);
    }
    // Fall back to direct storage only if trades prop was not provided
    try {
      const fallback = loadImportedTrades();
      if (Array.isArray(fallback) && fallback.length > 0) {
        return filterTradesForDashboardRange(fallback as Record<string, any>[], timeRange as any);
      }
    } catch {}
    return [];
  }, [trades, timeRange]);

  const { avgHoldWinMinutes, avgHoldLossMinutes, winSecondsPreferred, lossSecondsPreferred, winPortion, lossPortion } = useMemo(() => {
    const winTrades = activeTrades.filter((t) => {
      if (normalizeTradeStatus(t) === 'open') return false;
      const pnl = getTradePnl(t);
      const duration = getTradeDurationMins(t);
      return pnl > 0 && duration != null;
    });

    const lossTrades = activeTrades.filter((t) => {
      if (normalizeTradeStatus(t) === 'open') return false;
      const pnl = getTradePnl(t);
      const duration = getTradeDurationMins(t);
      return pnl < 0 && duration != null;
    });

    const calcAvg = (arr: typeof activeTrades) => {
      if (!arr || arr.length === 0) return 0;
      const sum = arr.reduce((s, r) => s + (getTradeDurationMins(r) || 0), 0);
      return sum / arr.length;
    };

    const winAvg = normalizeZero(calcAvg(winTrades));
    const lossAvg = normalizeZero(calcAvg(lossTrades));

    const prefersSeconds = (arr: typeof activeTrades) => {
      if (!arr || arr.length === 0) return false;
      const secsCount = arr.filter((r) => (getTradeDurationMins(r) || 0) < 1).length;
      return secsCount / arr.length > 0.5;
    };

    const totalHoldSum = winAvg + lossAvg;
    const winP = totalHoldSum > 0 ? winAvg / totalHoldSum : 0;
    const lossP = totalHoldSum > 0 ? lossAvg / totalHoldSum : 0;

    return {
      avgHoldWinMinutes: winAvg,
      avgHoldLossMinutes: lossAvg,
      winSecondsPreferred: prefersSeconds(winTrades),
      lossSecondsPreferred: prefersSeconds(lossTrades),
      winPortion: normalizeZero(winP),
      lossPortion: normalizeZero(lossP),
    };
  }, [activeTrades]);

  const formatDuration = (minutes: number, preferSeconds = false) => {
    if (!Number.isFinite(minutes) || minutes <= 0) return '—';
    if (minutes < 1 || preferSeconds) {
      const secs = Math.round(minutes * 60);
      return `${secs}s`;
    }
    const mins = Math.round(minutes);
    const days = Math.floor(mins / 1440);
    const hours = Math.floor((mins % 1440) / 60);
    const remMins = mins % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${remMins}m`;
    return `${remMins}m`;
  };

  useEffect(() => {
    try { console.debug('[WIDGET MOUNT] HoldTimeAnalysisWidget mounted'); } catch {}
  }, []);

  return (
    <div data-widget="HoldTimeAnalysisWidget" className="mt-0 relative h-full w-full rounded-[26px] flex flex-col text-white overflow-visible pt-0 p-0 sm:p-0 md:p-0" style={{ marginTop: '-3px' }}>
      <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-2 sm:gap-4">
        <div className="flex flex-col gap-0 w-full">
          <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.12em] text-emerald-600 flex items-center">
            <span className="whitespace-normal break-words">AVG WINNER</span>
          </div>
          <div className="text-lg sm:text-1xl md:text-2xl font-bold text-emerald-600 leading-tight mt-0">
            <span className="whitespace-nowrap truncate block">{avgHoldWinMinutes > 0 ? formatDuration(avgHoldWinMinutes, winSecondsPreferred) : '—'}</span>
          </div>
        </div>

        <div className="flex items-center justify-center w-full hidden sm:flex">
            <span className="text-lg font-thin text-zinc-700 uppercase tracking-wider">VS</span>
        </div>

        <div className="flex flex-col text-right w-full sm:col-start-3">
          <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.12em] text-red-500 flex items-center justify-end">
            <span className="whitespace-normal break-words">AVG LOSER</span>
          </div>
          <div className="text-lg sm:text-1xl md:text-2xl font-bold text-red-500 leading-tight mt-0">
            <span className="whitespace-nowrap truncate block">{avgHoldLossMinutes > 0 ? formatDuration(avgHoldLossMinutes, lossSecondsPreferred) : '—'}</span>
          </div>
        </div>
      </div>

      <div className="relative mt-2 h-1.5 sm:h-2 md:h-3 rounded-full bg-[#0f1118] border border-zinc-800/80 overflow-visible mb-2">
        <div
          className="absolute left-0 top-0 h-full bg-gradient-to-r from-emerald-600 via-emerald-600 to-emerald-600"
          style={{ width: `${winPortion * 100}%` }}
        />
        <div
          className="absolute right-0 top-0 h-full bg-gradient-to-r from-red-500 via-red-500 to-red-700"
          style={{ width: `${lossPortion * 100}%` }}
        />
        <div className="absolute inset-y-0 left-1/2 w-px bg-black/60" />
      </div>

      <div className="mt-3 sm:mt-0 flex items-center w-full gap-2">
        <span className="flex-1 text-[12px] sm:text-[12px] text-zinc-600 dark:text-zinc-400 min-w-0 whitespace-normal break-words uppercase text-left">WINNING DURATION</span>
        <span className="flex-1 text-[12px] sm:text-[12px] text-zinc-600 dark:text-zinc-400 min-w-0 whitespace-normal break-words uppercase text-right">LOSING DURATION</span>
      </div>
    </div>
  );
};

// Legacy name maintained for backwards compatibility
export const LargestLossWidget = HoldTimeAnalysisWidget;
export default HoldTimeAnalysisWidget;
